import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import Add from '@material-ui/icons/Add';
import MaterialIcon from '../../components/MaterialIcon';
import { POLYGON_TYPE_OPTIONS } from '../../components/geo';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}


function EnhancedTableHead(props) {
  const {
    classes,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    res,
    showPolyType,
    isPolygonList
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const headCells = [
    { id: 'name', numeric: false, disablePadding: true, label: res.NOME },
    ...(showPolyType
      ? [{ id: 'poly_type', numeric: false, disablePadding: false, label: res.TIPO }]
      : []),
    ...(isPolygonList
      ? [{ id: 'average_value', numeric: true, disablePadding: false, label: res.FLUXO_APLICACAO }]
      : []),
    { id: 'lng', numeric: true, disablePadding: false, label: res.LONGITUDE },
    { id: 'lat', numeric: true, disablePadding: false, label: res.LATITUDE },
    { id: 'img', numeric: false, disablePadding: false, label: res.IMAGEM },
    ...(isPolygonList
      ? [{ id: 'actions', numeric: false, disablePadding: false, label: res.ACOES, sortable: false }]
      : []),
  ];


  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortable === false ? headCell.label : (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight: {
    color: '#6c76ff',
    backgroundColor: '#d7d9e0',
  },
  title: {
    flex: '1 1 100%',
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();
  const { numSelected, type, add, loadFile, legacyLoadFile, remove, res, hasSavedImport, openSavedImport } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      {numSelected > 0 ? (
        <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
          {numSelected} selected
        </Typography>
      ) : (
        <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
          {res.DADOS_GEOGRAFICOS} - {type}
        </Typography>
      )}

      {numSelected > 0 ? (
        <Tooltip title={res.DELETE}>
          <IconButton aria-label={res.DELETE}>
            <DeleteIcon onClick={remove} />
          </IconButton>
        </Tooltip>
      ) : (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {legacyLoadFile && (
            <label
              htmlFor={`contained-button-file-filter-${type[0]}${type[1]}`}
              style={{ marginBottom: 0, display: 'inline' }}
            >
              <input
                accept=".kml,.kmz,.zip"
                style={{ display: 'none' }}
                id={`contained-button-file-filter-${type[0]}${type[1]}`}
                type="file"
                multiple
                onChange={legacyLoadFile}
              />
              <span
                className="MuiButtonBase-root MuiIconButton-root"
                tabIndex="0"
                type="button"
                aria-label="Anexar"
                title={res.IMPORTAR_AREAS}
              >
                <MaterialIcon icon="attach_file" />
                <span className="MuiTouchRipple-root"></span>
              </span>
            </label>
          )}
          <label htmlFor={hasSavedImport ? undefined : `contained-button-file-${type[0]}${type[1]}`} onClick={hasSavedImport ? openSavedImport : undefined} style={{ marginBottom: 0, display: 'inline' }}>
            <input
              accept=".kml,.kmz,.shp,.zip"
              style={{ display: 'none' }}
              id={`contained-button-file-${type[0]}${type[1]}`}
              type="file" onChange={loadFile}
            />
            <label htmlFor={hasSavedImport ? undefined : `contained-button-file-${type[0]}${type[1]}`}>
              <span className="MuiButtonBase-root MuiIconButton-root" tabIndex="0" type="button" aria-label="filter list" title={res.IMPORTAR_ZONA_VAZAO}>

                <MaterialIcon icon="filter_list" />
                <span className="MuiTouchRipple-root"></span>
              </span>
            </label>
          </label>
          <Tooltip title={res.ADD}>
            <IconButton aria-label={res.ADD} onClick={(event) => { add() }}>
              <Add />
            </IconButton>
          </Tooltip>
        </span>
      )}
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

export default function EnhancedTable(props) {
  const { list, type, upload, legacyUpload, edit, update, res, showPolyType, hasSavedImport, openSavedImport } = props;
  const classes = useStyles();
  const isPolygonList = Boolean(showPolyType || type === res.POLIGONOS);
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('calories');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [editingGuid, setEditingGuid] = React.useState(null);
  const [draftName, setDraftName] = React.useState('');
  const [draftType, setDraftType] = React.useState('N');
  const [draftFlow, setDraftFlow] = React.useState('');
  const [flowError, setFlowError] = React.useState(false);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = list.map((n) => n.guid);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, guid) => {
    const selectedIndex = selected.indexOf(guid);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, guid);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (guid) => selected.indexOf(guid) !== -1;

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, list.length - page * rowsPerPage);

  const getFlowValue = (row) => {
    if (row.average_value !== undefined && row.average_value !== null) return row.average_value;
    if (row.flow_rate !== undefined && row.flow_rate !== null) return row.flow_rate;
    return '';
  };

  const startInlineEdit = (row) => {
    const polygonType = row.poly_type === 'C' ? 'F' : (row.poly_type || row.type);
    setEditingGuid(row.guid);
    setDraftName(row.name !== undefined && row.name !== null ? String(row.name) : '');
    setDraftType(POLYGON_TYPE_OPTIONS.some(option => option.value === polygonType) ? polygonType : 'N');
    setDraftFlow(String(getFlowValue(row)));
    setFlowError(false);
  };

  const cancelInlineEdit = () => {
    setEditingGuid(null);
    setDraftName('');
    setDraftType('N');
    setDraftFlow('');
    setFlowError(false);
  };

  const saveInlineEdit = (row) => {
    const normalizedFlow = draftFlow.trim().replace(',', '.');
    const flowValue = normalizedFlow === '' ? null : Number(normalizedFlow);

    if (normalizedFlow !== '' && !Number.isFinite(flowValue)) {
      setFlowError(true);
      return;
    }

    const updatedList = list.map((item) => (
      item.guid === row.guid
        ? { ...item, name: draftName, poly_type: draftType, average_value: flowValue }
        : item
    ));

    update(updatedList);
    cancelInlineEdit();
  };

  const remove = () => {
    if (selected.length > 0) {
      let temp_list = list.filter((item) => selected.indexOf(item.guid) === -1);
      setSelected([]);
      let deleted = '';
      let delList = list.filter((item) => selected.indexOf(item.guid) > -1 && item.id);
      for (let i = 0; i < delList.length; i++) {
        deleted += (i > 0 ? ',' : '') + delList[i].id;
      }
      update(temp_list, deleted);
    }
  }

  return (
    <div className={classes.root}>
      <Paper elevation={3} className={classes.paper}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          res={res}
          type={type}
          loadFile={upload}
          legacyLoadFile={legacyUpload}
          hasSavedImport={hasSavedImport}
          openSavedImport={openSavedImport}
          add={edit}
          remove={remove}
        />
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size="small"
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            classes={classes}
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={list.length}
            res={res}
            showPolyType={showPolyType}
            isPolygonList={isPolygonList}
          />
          <TableBody>
            {stableSort(list, getComparator(order, orderBy))
              .slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage)
              .map((row, index) => {
                const isItemSelected = isSelected(row.guid);
                const isEditing = editingGuid === row.guid;
                const labelId = `enhanced-table-checkbox-${index}`;
                const click = (event) => handleClick(event, row.guid);
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.guid}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox" onClick={click}>
                      <Checkbox
                        checked={isItemSelected}
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding={isEditing ? 'default' : 'none'}
                      onClick={isEditing ? undefined : click}
                    >
                      {isEditing ? (
                        <TextField
                          value={draftName}
                          onChange={(event) => setDraftName(event.target.value)}
                          inputProps={{ 'aria-label': res.NOME }}
                          fullWidth
                        />
                      ) : row.name}
                    </TableCell>
                    {showPolyType && (
                      <TableCell onClick={isEditing ? undefined : click}>
                        {isEditing ? (
                          <TextField
                            select
                            value={draftType}
                            onChange={(event) => setDraftType(event.target.value)}
                            inputProps={{ 'aria-label': res.TIPO || 'Tipo' }}
                            fullWidth
                          >
                            {POLYGON_TYPE_OPTIONS.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          POLYGON_TYPE_OPTIONS.find(option => (
                            option.value === (row.poly_type === 'C' ? 'F' : (row.poly_type || row.type))
                          ))?.label || ''
                        )}
                      </TableCell>
                    )}
                    {isPolygonList && (
                      <TableCell align="right" onClick={isEditing ? undefined : click}>
                        {isEditing ? (
                          <TextField
                            value={draftFlow}
                            onChange={(event) => {
                              setDraftFlow(event.target.value);
                              setFlowError(false);
                            }}
                            error={flowError}
                            inputProps={{
                              'aria-label': res.VAZAO || res.FLUXO || 'Vazão',
                              inputMode: 'decimal'
                            }}
                            style={{ width: '7rem' }}
                          />
                        ) : getFlowValue(row)}
                      </TableCell>
                    )}
                    <TableCell align="right" onClick={click}>{row.lng}</TableCell>
                    <TableCell align="right" onClick={click}>{row.lat}</TableCell>
                    <TableCell component="a" onClick={(event) => { edit(row.guid) }}>
                      link
                    </TableCell>
                    {isPolygonList && (
                      <TableCell>
                        {isEditing ? (
                          <span style={{ display: 'flex', gap: '0.5rem' }}>
                            <Button size="small" onClick={cancelInlineEdit}>
                              {res.CANCELAR || 'Cancelar'}
                            </Button>
                            <Button
                              size="small"
                              color="primary"
                              variant="contained"
                              onClick={() => saveInlineEdit(row)}
                            >
                              {res.SALVAR || 'Salvar'}
                            </Button>
                          </span>
                        ) : (
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => startInlineEdit(row)}
                          >
                            {res.EDITAR || 'Editar'}
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 33 * emptyRows }}>
                <TableCell colSpan={5 + (showPolyType ? 1 : 0) + (isPolygonList ? 2 : 0)} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={list.length}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            'aria-label': res.PAGINA_ANTERIOR,
          }}
          nextIconButtonProps={{
            'aria-label': res.PROXIMA_PAGINA,
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}
